import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function BalanceSheet() {
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
    const [balanceSheetData, setBalanceSheetData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadBalanceSheet();
    }, [asOfDate]);

    const loadBalanceSheet = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getBalanceSheet({
                as_of_date: asOfDate
            });

            if (response.success) {
                setBalanceSheetData(response.data);
            } else {
                setError(response.message || 'Failed to load balance sheet');
            }
        } catch (err) {
            console.error('Error loading balance sheet:', err);
            setError('An error occurred while loading the balance sheet');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Balance Sheet</h1>
                </div>
                <p className="text-sm text-slate-600">Financial position statement</p>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="max-w-xs">
                    <label className="block text-sm font-bold text-slate-700 mb-2">As of Date</label>
                    <input
                        type="date"
                        value={asOfDate}
                        onChange={(e) => setAsOfDate(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    />
                </div>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading balance sheet...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!loading && balanceSheetData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Assets */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-4">
                            <h2 className="text-lg font-bold text-indigo-900">Assets</h2>
                        </div>
                        <div className="p-6">
                            {balanceSheetData.assets && balanceSheetData.assets.length > 0 ? (
                                <div className="space-y-3">
                                    {balanceSheetData.assets.map((asset) => (
                                        <div key={asset.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                                            <span className="text-sm text-slate-700">{asset.name}</span>
                                            <span className="text-sm font-bold text-slate-900">₹{(asset.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    ))}
                                    <div className="flex justify-between items-center pt-3 border-t-2 border-indigo-200">
                                        <span className="font-bold text-indigo-900">Total Assets</span>
                                        <span className="text-lg font-bold text-indigo-900">₹{(balanceSheetData.total_assets || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">No assets</p>
                            )}
                        </div>
                    </div>

                    {/* Liabilities & Equity */}
                    <div className="space-y-6">
                        {/* Liabilities */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="bg-rose-50 border-b border-rose-100 px-6 py-4">
                                <h2 className="text-lg font-bold text-rose-900">Liabilities</h2>
                            </div>
                            <div className="p-6">
                                {balanceSheetData.liabilities && balanceSheetData.liabilities.length > 0 ? (
                                    <div className="space-y-3">
                                        {balanceSheetData.liabilities.map((liability) => (
                                            <div key={liability.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                                                <span className="text-sm text-slate-700">{liability.name}</span>
                                                <span className="text-sm font-bold text-slate-900">₹{(liability.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 italic">No liabilities</p>
                                )}
                            </div>
                        </div>

                        {/* Equity */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-4">
                                <h2 className="text-lg font-bold text-emerald-900">Equity</h2>
                            </div>
                            <div className="p-6">
                                {balanceSheetData.equity && balanceSheetData.equity.length > 0 ? (
                                    <div className="space-y-3">
                                        {balanceSheetData.equity.map((equity) => (
                                            <div key={equity.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                                                <span className="text-sm text-slate-700">{equity.name}</span>
                                                <span className="text-sm font-bold text-slate-900">₹{(equity.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                            <span className="text-sm text-slate-700">Net Profit</span>
                                            <span className="text-sm font-bold text-emerald-600">₹{(balanceSheetData.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="flex justify-between items-center pt-3 border-t-2 border-emerald-200">
                                            <span className="font-bold text-emerald-900">Total Liabilities & Equity</span>
                                            <span className="text-lg font-bold text-emerald-900">₹{(balanceSheetData.total_liabilities_equity || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 italic">No equity</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BalanceSheet;
